// pfs-field.ts — Benennungs-Wrapper (PFS-652 Phase 1, „Label ↔ Control entkoppeln").
//
// Das Control (<pfs-text-field>/-select-field>/-textarea-field>) ist NACKT: nur
// Wert + Events, kein Label. Wer ein Feld benennen will, KOMPONIERT es mit
// diesem Wrapper — der einzige Weg zu Label/Hint/Fehler. Damit ist das Label
// komplett vom Control entkoppelt (Control bleibt in Tabellenzelle/Toolbar ohne
// Ballast nutzbar).
//
//   <pfs-field data-label="E-Mail" data-hint="…"><pfs-text-field/></pfs-field>
//
// LAYOUT ist ein KONTEXT-Belang, kein Feld-Belang — und RESPONSIVENESS steckt
// intrinsisch drin (kein Extra-Modus):
//   - stacked = universelle Basis (Label über Control; kippt nie).
//   - inline  = Label vor Control; faellt bei schmalem Container auf stacked zurueck.
//   - aligned = löst das Feld via :host{display:contents} ins Grid der Gruppe auf
//     (geteilte Label-Spalte über Zeilen); faellt bei schmalem Container auf stacked
//     (volle Zeile) zurueck.
// inline/aligned kippen per @container nach CONTAINER-Breite (nicht Viewport) — die
// @container-Regel steht in DIESER Shadow-CSS und quert die Grenze zum Container
// (Gruppe/Card). Der Kontext setzt die Anordnung EINMAL via `--pfs-field-label`
// (vererbt); jedes Feld liest sie beim Connect, ein explizites data-layout gewinnt.
//
// API:
//   Attribute: data-label · data-hint · data-error · data-required · data-name ·
//     data-layout (stacked|inline|aligned) · data-span ("all" = volle Grid-Zeile).
//   Property: el.control (das geslottete Control) · el.value (Proxy auf control.value,
//     pre-connect-sicher).
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";

/** Ein geslottetes Control mit Wert + optional focus(). */
type SlottedControl = HTMLElement & { value?: string; focus?: () => void };

/** Typisierte Consumer-Sicht auf <pfs-field>. */
export interface PfsFieldElement extends HTMLElement {
  readonly control: SlottedControl | null;
  value: string;
}

// Abstände AUSSCHLIESSLICH via gap (kein margin) — Label↔Control-Abstand =
// --pfs-space-label. Skala überschreibbar via Token, Default = Plattform-Wert.
const STYLES = `
:host { display: flex; flex-direction: column; gap: var(--pfs-space-label, 0.5rem); }
.lbl { font-size: 0.8rem; font-weight: 600; line-height: 1.3; color: var(--pfs-color-on-surface); }
.lbl:empty { display: none; }
.req { color: var(--pfs-color-danger); margin-left: 0.15rem; }
.ctl { display: block; }
.msg { font-size: 0.78rem; }
.msg:empty { display: none; }
.msg.error { color: var(--pfs-color-danger); }
.msg.hint { color: var(--pfs-color-on-surface-muted); }

/* inline & aligned sind INTRINSISCH RESPONSIV: bei zu schmalem Container fallen
   sie auf die Basis (stacked) zurueck, ab dem Schwellwert kippen sie. stacked
   selbst braucht nie zu kippen. @container quert die Shadow-Grenze zum Container. */

/* inline: schmal = gestapelt (Basis); breit = Label vor Control (flex row). */
@container (min-width: 30rem) {
  :host([data-mode="inline"]) { flex-direction: row; align-items: center; }
  :host([data-mode="inline"]) .lbl { white-space: nowrap; }
  :host([data-mode="inline"]) .ctl { flex: 1; }
  :host([data-mode="inline"]) .msg { display: none; }
}

/* aligned: schmal = volle Zeile (gestapelt, Basis); breit = Host loest sich via
   display:contents ins Grid der Gruppe auf — der Label|Control-Abstand ist dann
   der col-gap der Gruppe (--pfs-space-col). Validiert: contents traegt ueber die
   Shadow-Grenze. */
:host([data-mode="aligned"]) { grid-column: 1 / -1; }
@container (min-width: 30rem) {
  :host([data-mode="aligned"]) { display: contents; }
  :host([data-mode="aligned"]) .lbl { white-space: nowrap; justify-self: start; align-self: center; }
  :host([data-mode="aligned"]) .msg { display: none; }
}
/* volle Zeile erzwingen (Abschnitts-Notiz / breites Feld): bleibt immer gestapelt
   (flex column), loest sich NIE auf, spannt beide Spalten. */
:host([data-mode="aligned"][data-span="all"]) { grid-column: 1 / -1; }
@container (min-width: 30rem) {
  :host([data-mode="aligned"][data-span="all"]) { display: flex; }
}
`;

function slottedControl(host: HTMLElement): SlottedControl | null {
  // Light-DOM-Kind — existiert VOR connect (pre-connect-sicher), anders als der
  // Shadow-Slot. Erstes Element-Kind = das Control.
  return (host.firstElementChild as SlottedControl | null) ?? null;
}

defineComponent({
  tagName: "pfs-field",
  mixins: [withShadow()],
  // eager: wird von server-gerenderten Cut-App-WCs SYNCHRON konsumiert (buildCard
  // erzeugt <pfs-field> + liest .value sofort) → muss ab Import registriert sein.
  eager: true,
  // .control + .value ab Konstruktion (pre-connect-sicher, proxied aufs Control).
  created(host) {
    Object.defineProperty(host, "control", {
      configurable: true,
      get: () => slottedControl(host),
    });
    Object.defineProperty(host, "value", {
      configurable: true,
      get: () => slottedControl(host)?.value ?? "",
      set: (v: unknown) => {
        const c = slottedControl(host);
        if (c) c.value = v == null ? "" : String(v);
      },
    });
  },
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;
    const lbl = document.createElement("label");
    lbl.className = "lbl";
    lbl.setAttribute("part", "label");
    const ctl = document.createElement("div");
    ctl.className = "ctl";
    ctl.setAttribute("part", "control");
    const slot = document.createElement("slot");
    ctl.appendChild(slot);
    const msg = document.createElement("p");
    msg.className = "msg";
    msg.setAttribute("part", "message");
    root.append(style, lbl, ctl, msg);

    // Label-Klick fokussiert das Control (delegatesFocus an den Controls leitet
    // an den inneren Input weiter). mousedown + preventDefault haelt den Fokus.
    lbl.addEventListener("mousedown", (e) => {
      e.preventDefault();
      slottedControl(host)?.focus?.();
    });

    // data-mode = aufgeloestes Layout: explizites data-layout ODER geerbtes
    // --pfs-field-label (Kontext) ODER "stacked".
    const resolveMode = (): void => {
      let m = host.dataset.layout;
      if (!m) m = getComputedStyle(host).getPropertyValue("--pfs-field-label").trim() || "stacked";
      if (host.getAttribute("data-mode") !== m) host.setAttribute("data-mode", m);
    };

    const paint = (): void => {
      const labelText = host.dataset.label ?? "";
      lbl.textContent = labelText;
      if (host.dataset.required != null && labelText) {
        const s = document.createElement("span");
        s.className = "req";
        s.textContent = "*";
        lbl.appendChild(s);
      }
      // a11y: das Control bekommt seinen Namen vom Label (for/id traegt nicht
      // ueber die Control-Shadow-Grenze). Nur setzen, wenn das Control keinen
      // eigenen Namen mitbringt.
      const c = slottedControl(host);
      if (c && labelText && !c.hasAttribute("aria-label") && c.dataset.label == null) {
        c.setAttribute("aria-label", labelText);
      }
      const err = (host.dataset.error ?? "").trim();
      const hint = (host.dataset.hint ?? "").trim();
      if (err) {
        msg.textContent = err;
        msg.className = "msg error";
        c?.setAttribute?.("data-invalid", "");
      } else {
        c?.removeAttribute?.("data-invalid");
        if (hint) {
          msg.textContent = hint;
          msg.className = "msg hint";
        } else {
          msg.textContent = "";
          msg.className = "msg";
        }
      }
    };

    resolveMode();
    paint();

    const obs = new MutationObserver(() => {
      resolveMode();
      paint();
    });
    obs.observe(host, {
      attributes: true,
      attributeFilter: ["data-label", "data-hint", "data-error", "data-required", "data-name", "data-layout"],
    });
    // Control-Wechsel (Slot) → Name/Invalid neu setzen.
    slot.addEventListener("slotchange", paint);
    ctx.cleanup.push(() => obs.disconnect());
  },
});
